import { productEvent } from "@shared/analytics/productEvents";
import SOLIcon from "@shared/assets/icons/tokens/sol.svg";
import USDGIcon from "@shared/assets/icons/tokens/usdg.svg";
import USDCIcon from "@shared/assets/icons/USDC.svg";
import USDTIcon from "@shared/assets/icons/USDT.svg";
import USDvIcon from "@shared/assets/logos/usdv-logomark.png";
import { formatCurrency } from "@solomon-apps/shared";
import type { Position } from "@solomon-apps/shared";
import {
    type BarSegment,
    ProportionalBar
} from "@solomon-labs/design/components/data";
import { ArrowShortUpRightIcon } from "@solomon-labs/design/components/icons";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    ButtonIcon,
    Skeleton,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import type { GraphColor } from "@solomon-labs/design/tokens/graph";
import { graphColorValue } from "@solomon-labs/design/tokens/graph";
import React from "react";

import type {
    ReserveAssetRow,
    ReserveClassRow
} from "@/hooks/stats/useReservesData";

import { solscanAccountUrl } from "./explorerLinks";
import { ReserveHistoryChart } from "./ReserveHistoryChart";
import { ReservesEmptyState } from "./ReservesEmptyState";

/**
 * The graph palette is tuned for charcoal surfaces: `graph-primary` is a cream
 * that reads at 1.1:1 against Paper and all but disappears. This composition
 * sits flat on the page ground, so the palette is re-ordered by contrast
 * against `surface-ground` and the readable tokens are handed out first.
 */
const LIGHT_SURFACE_GRAPH_COLORS: readonly GraphColor[] = [
    "graph-quinary",
    "graph-denary",
    "graph-quaternary",
    "graph-octonary",
    "graph-tertiary",
    "graph-septenary",
    "graph-nonary",
    "graph-secondary",
    "graph-senary",
    "graph-primary"
];

function paletteColor(index: number): GraphColor {
    return LIGHT_SURFACE_GRAPH_COLORS[
        index % LIGHT_SURFACE_GRAPH_COLORS.length
    ] as GraphColor;
}

/** Colour identifies a token, not the class it sits in, so the assignment runs
 *  once across every asset on the page. Largest first, so the biggest holdings
 *  take the most readable end of the palette. */
function assetColors(classes: ReserveClassRow[]): Map<string, GraphColor> {
    const symbols = classes
        .flatMap((row) => row.assets)
        .slice()
        .sort((a, b) => b.amount - a.amount)
        .map((asset) => asset.symbol);
    return new Map(
        symbols.map((symbol, index) => [symbol, paletteColor(index)])
    );
}

/** "Binance BTC/USD inverse perpetual" from the parts the feed supplies. */
function describePosition(position: Position): string {
    const parts = [
        position.exchange?.trim(),
        `${(position.baseAsset ?? "").toUpperCase()}/${(position.quoteAsset ?? "").toUpperCase()}`,
        position.marketType?.trim().toLowerCase(),
        position.futuresType?.trim().toLowerCase()
    ];
    return parts
        .filter((part): part is string => Boolean(part) && part !== "/")
        .join(" ");
}

function notionalOf(position: Position): number {
    const value = Number(position.notionalUsd ?? "0");
    return Number.isFinite(value) && value > 0 ? value : 0;
}

/** Column geometry shared by the header, the class rows and the asset rows, so
 *  every figure on the page sits in the same two columns. */
const AMOUNT_COL = "w-32 shrink-0 text-right";
const SHARE_COL = "w-16 shrink-0 text-right";
const VERIFY_COL = "absolute -right-7 top-1/2 w-6 -translate-y-1/2";

/** Symbol to logo. `tokenMetadata` keys its registry by mint address and a
 *  composition row carries only a symbol, so the mapping is stated here.
 *  A symbol with no logo renders its text alone rather than a placeholder. */
const ASSET_LOGOS: Readonly<Record<string, string>> = {
    SOL: SOLIcon,
    USDC: USDCIcon,
    USDT: USDTIcon,
    USDG: USDGIcon,
    USDv: USDvIcon
};

/** Every highlight on this page is a state change on a paint property, so one
 *  duration and one named easing cover the whole interaction. 200ms is the
 *  `motion/durations` figure for hover and focus state changes, and
 *  `motion-reduce` drops the tween while leaving the state itself intact. */
const HIGHLIGHT_TRANSITION =
    "transition-colors duration-200 ease-[var(--ease-out-quad)] motion-reduce:transition-none";
const SCRIM_TRANSITION =
    "transition-opacity duration-200 ease-[var(--ease-out-quad)] motion-reduce:transition-none";

/** How far a segment recedes when the reader is asking about a different one.
 *  Enough that the asked-for colour separates, not so much that the rest of the
 *  split stops being readable as a whole. */
const SCRIM_OPACITY = 0.62;

function AssetSwatch({ color }: { color: GraphColor }): React.ReactElement {
    return (
        <span
            aria-hidden
            className="size-2.5 shrink-0"
            style={{ backgroundColor: graphColorValue(color) }}
        />
    );
}

function AssetLogo({ symbol }: { symbol: string }): React.ReactElement | null {
    const src = ASSET_LOGOS[symbol];
    if (!src) return null;
    return <img src={src} alt="" aria-hidden className="size-4 shrink-0" />;
}

interface AssetNameProps {
    symbol: string;
    className?: string | undefined;
}

/** Logo and symbol are two renderings of one name, so they sit tight together;
 *  the colour swatch is a separate piece of information and stands off by a
 *  step more. Shared by both asset cells so the two cannot drift. */
function AssetName({ symbol, className }: AssetNameProps): React.ReactElement {
    return (
        <span className="inline-flex items-center gap-1 min-w-0">
            <AssetLogo symbol={symbol} />
            <Text variant="small" render={<span />} className={className}>
                {symbol}
            </Text>
        </span>
    );
}

interface VerifyButtonProps {
    venue: ReserveAssetRow["venues"][number];
    symbol: string;
}

/** The onchain proof for a holding, if there is an address to point at.
 *  Renders nothing when there is not, rather than a dead affordance. */
function VerifyButton({
    venue,
    symbol
}: VerifyButtonProps): React.ReactElement | null {
    const { verifyAddress } = venue;
    if (!verifyAddress) return null;

    return (
        // Icon-only, so this is `ButtonIcon`'s case. It ships no `render`, so
        // it is always a native `<button>` and this is no longer an anchor:
        // cmd/middle-click to a new tab and right-click "copy link" are gone,
        // and a screen reader now announces a button. That trade is deliberate
        // — see the commit — so please do not "fix" it back to an `<a>`.
        // `window.open` keeps the `noopener,noreferrer` the `rel` used to
        // carry. `transparent` over the `inverse-raised` default, which would
        // put a chip in a row with no other surface. Same call as the address
        // rows on the Security tab, so the two match.
        <ButtonIcon
            variant="transparent"
            size="lg"
            className={VERIFY_COL}
            icon={<ArrowShortUpRightIcon />}
            aria-label={`Verify the ${symbol} holding at ${venue.label} onchain`}
            onClick={() => {
                productEvent("stats:link_clicked", {
                    entry_point: "stats_page",
                    section: "reserves",
                    link_type: "holding_explorer"
                });
                window.open(
                    solscanAccountUrl(verifyAddress),
                    "_blank",
                    "noopener,noreferrer"
                );
            }}
        />
    );
}

interface CompositionBarProps {
    segments: BarSegment[];
    loading: boolean;
    /** The symbol the reader is currently asking about, from either side. */
    activeSymbol: string | null;
    onHover: (symbol: string | null) => void;
    onFocusChange: (symbol: string | null) => void;
    /** Open whichever class holds this symbol, so the row it names is on screen. */
    onReveal: (symbol: string) => void;
}

/**
 * The fill bar, plus the interaction layer the DS bar does not have.
 *
 * `ProportionalBar` takes `segments`, `height`, `loading` and `surface` and
 * nothing else, and the segment `<div>`s it emits carry only an inline `style`:
 * no class, no `data-slot`, no handlers. Its own intent file says so outright
 * ("this bar has no interaction surface at all"). Rather than fork it or select
 * into its private markup, the interactive track sits above it as a sibling,
 * sized from the same `segments` array, and de-emphasises the segments the
 * reader is not asking about by fading a `surface-ground` scrim over them.
 *
 * De-emphasis rather than emphasis because a segment has no edges of its own:
 * the thin ones here are two or three pixels wide, so a border or an inset
 * marker would either vanish or swallow the segment. The only contrast
 * available inside a continuous strip is its neighbours receding. It also
 * spends no new colour, which keeps the page's one earned Ember accent
 * (`color/ember-once`) where it already is.
 */
function CompositionBar({
    segments,
    loading,
    activeSymbol,
    onHover,
    onFocusChange,
    onReveal
}: CompositionBarProps): React.ReactElement {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0);

    return (
        <div className="relative">
            <ProportionalBar
                segments={segments}
                loading={loading}
                surface="light"
            />

            {!loading && total > 0 && (
                <TooltipProvider delay={80}>
                    <div className="absolute inset-0 flex">
                        {segments.map((segment) => {
                            const share = (segment.value / total) * 100;
                            const dimmed =
                                activeSymbol !== null &&
                                activeSymbol !== segment.label;
                            return (
                                <Tooltip key={segment.label}>
                                    <TooltipTrigger
                                        render={
                                            <button
                                                type="button"
                                                data-segment={segment.label}
                                                // Same normalization the bar
                                                // underneath applies, from the
                                                // same array, so the two tracks
                                                // align.
                                                style={{ width: `${share}%` }}
                                                className="relative shrink-0 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground-base"
                                                aria-label={`Show ${segment.label}, ${share.toFixed(1)}% of reserves`}
                                                onPointerEnter={() =>
                                                    onHover(segment.label)
                                                }
                                                onPointerLeave={() =>
                                                    onHover(null)
                                                }
                                                onFocus={() =>
                                                    onFocusChange(segment.label)
                                                }
                                                onBlur={() =>
                                                    onFocusChange(null)
                                                }
                                                onClick={() => {
                                                    productEvent(
                                                        "stats:selected",
                                                        {
                                                            entry_point:
                                                                "stats_page",
                                                            section:
                                                                "reserve_composition",
                                                            target: segment.label
                                                        }
                                                    );
                                                    onReveal(segment.label);
                                                }}
                                            >
                                                <span
                                                    aria-hidden
                                                    data-scrim={segment.label}
                                                    className={`absolute inset-0 bg-surface-ground ${SCRIM_TRANSITION}`}
                                                    style={{
                                                        opacity: dimmed
                                                            ? SCRIM_OPACITY
                                                            : 0
                                                    }}
                                                />
                                            </button>
                                        }
                                    />
                                    <TooltipContent side="top" maxWidth="sm">
                                        <span className="flex flex-col gap-0.5">
                                            <span>{segment.label}</span>
                                            <span>
                                                {formatCurrency(segment.value)},{" "}
                                                {share.toFixed(1)}%
                                            </span>
                                        </span>
                                    </TooltipContent>
                                </Tooltip>
                            );
                        })}
                    </div>
                </TooltipProvider>
            )}
        </div>
    );
}

interface AssetDetailProps {
    showVerification?: boolean;
    asset: ReserveAssetRow;
    hedges: Position[];
    color: GraphColor;
    /** This asset is the one the reader is asking about, from either side. */
    active: boolean;
    onHover: (symbol: string | null) => void;
    onFocusChange: (symbol: string | null) => void;
}

/** One total row per asset. Non-stablecoins can also show venue rows when held
 *  in multiple places. The hedge rides on the asset symbol. */
function AssetDetail({
    showVerification = true,
    asset,
    hedges,
    color,
    active,
    onHover,
    onFocusChange
}: AssetDetailProps): React.ReactElement {
    const singleVenue = asset.venues.length === 1 ? asset.venues[0] : undefined;
    const showVenueRows =
        asset.symbol !== "USDC" &&
        asset.symbol !== "USDT" &&
        asset.venues.length > 1;

    const symbolCell =
        hedges.length > 0 ? (
            <TooltipProvider delay={300}>
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <span className="w-28 shrink-0 inline-flex items-center gap-2 cursor-help">
                                <AssetSwatch color={color} />
                                <AssetName
                                    symbol={asset.symbol}
                                    className="underline decoration-dotted underline-offset-4"
                                />
                            </span>
                        }
                    />
                    <TooltipContent>
                        {hedges
                            .map(
                                (hedge) =>
                                    `Hedged by ${describePosition(hedge)}, ${formatCurrency(notionalOf(hedge))} notional`
                            )
                            .join(". ")}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        ) : (
            <span className="w-28 shrink-0 inline-flex items-center gap-2">
                <AssetSwatch color={color} />
                <AssetName symbol={asset.symbol} />
            </span>
        );

    return (
        // The negative margin plus matching padding widens the wash past the
        // figures without moving a single column.
        <div
            data-asset-row={asset.symbol}
            data-active={active}
            className={`flex flex-col -mx-2 px-2 ${HIGHLIGHT_TRANSITION} ${active ? "bg-surface-sunken" : "bg-transparent"}`}
            onPointerEnter={() => onHover(asset.symbol)}
            onPointerLeave={() => onHover(null)}
            onFocus={() => onFocusChange(asset.symbol)}
            onBlur={() => onFocusChange(null)}
        >
            <div className="relative flex items-center gap-4 py-2.5">
                {symbolCell}
                <span className="flex-1 min-w-0" aria-hidden />
                <Text variant="number" render={<span />} className={AMOUNT_COL}>
                    {asset.amountDisplay}
                </Text>
                <Text variant="number" render={<span />} className={SHARE_COL}>
                    {asset.share}
                </Text>
                {showVerification && singleVenue && (
                    <VerifyButton venue={singleVenue} symbol={asset.symbol} />
                )}
            </div>

            {showVenueRows &&
                asset.venues.map((venue) => (
                    <div
                        key={venue.label}
                        className="relative flex items-center gap-4 py-2.5"
                    >
                        <span className="w-28 shrink-0" aria-hidden />
                        <span className="flex-1 min-w-0" aria-hidden />
                        <Text
                            variant="number"
                            render={<span />}
                            className={AMOUNT_COL}
                        >
                            {venue.amountDisplay}
                        </Text>
                        <span className={SHARE_COL} aria-hidden />
                        {showVerification && (
                            <VerifyButton venue={venue} symbol={asset.symbol} />
                        )}
                    </div>
                ))}
        </div>
    );
}

interface ReserveCompositionProps {
    classes: ReserveClassRow[];
    /** Derivatives from the protocol feed, folded onto the asset they hedge. */
    positions: Position[];
    loading: boolean;
}

/**
 * One hierarchy over the reserve holdings: asset class, then asset, then the
 * venue holding it, with the hedge that sits against an asset folded onto that
 * asset's row. Replaces the three overlapping tables the tab used to carry
 * ("By location", "By asset class", and "Strategy"), each of which listed the
 * same dollars against a different total.
 *
 * Every share here is of the itemized total, never of USDv supply, so no two
 * percentages on the page answer to different denominators.
 */
export function ReserveComposition({
    classes,
    positions,
    loading
}: ReserveCompositionProps): React.ReactElement {
    const colorBySymbol = React.useMemo(() => assetColors(classes), [classes]);

    /** Hover and focus are tracked apart, and focus wins. A tap on a segment
     *  fires pointerenter, then focus, then pointerleave, so folding the two
     *  into one value would clear the highlight the moment a touch user lifted
     *  their finger. */
    const [hoveredSymbol, setHoveredSymbol] = React.useState<string | null>(
        null
    );
    const [focusedSymbol, setFocusedSymbol] = React.useState<string | null>(
        null
    );
    const activeSymbol = focusedSymbol ?? hoveredSymbol;

    const [openClasses, setOpenClasses] = React.useState<string[]>([]);

    const handleOpenChange = React.useCallback((next: unknown[]): void => {
        productEvent("stats:section_toggled", {
            entry_point: "stats_page",
            section: "reserve_classes",
            item_count: next.length
        });
        setOpenClasses(
            next.filter((item): item is string => typeof item === "string")
        );
    }, []);

    const classBySymbol = React.useMemo(() => {
        const map = new Map<string, string>();
        for (const row of classes) {
            for (const asset of row.assets) map.set(asset.symbol, row.label);
        }
        return map;
    }, [classes]);

    /** Clicking a segment opens the class that holds it, so the row the segment
     *  names is on screen to receive the highlight. This is the whole of the
     *  touch story: without a hover state, the tap is what connects the two. */
    const revealAsset = React.useCallback(
        (symbol: string): void => {
            const owner = classBySymbol.get(symbol);
            if (!owner) return;
            setOpenClasses((open) =>
                open.includes(owner) ? open : [...open, owner]
            );
        },
        [classBySymbol]
    );

    /** A segment whose class is collapsed has no row to light up. Expanding on
     *  hover would move the page under the cursor, and doing nothing would
     *  leave the segment silently dead, so the collapsed class header takes the
     *  highlight instead: the smallest true statement is "it is in here", and
     *  clicking the segment promotes that to the row itself. */
    const flaggedClass = activeSymbol
        ? classBySymbol.get(activeSymbol)
        : undefined;

    const segments: BarSegment[] = React.useMemo(
        () =>
            classes
                .flatMap((row) => row.assets)
                .map((asset) => ({
                    label: asset.symbol,
                    value: asset.amount,
                    color: colorBySymbol.get(asset.symbol) ?? paletteColor(0)
                })),
        [classes, colorBySymbol]
    );

    const hedgesByAsset = React.useMemo(() => {
        const map = new Map<string, Position[]>();
        for (const position of positions) {
            if (notionalOf(position) <= 0) continue;
            const symbol = (position.baseAsset ?? "").trim().toUpperCase();
            if (!symbol) continue;
            map.set(symbol, [...(map.get(symbol) ?? []), position]);
        }
        return map;
    }, [positions]);

    const heldSymbols = React.useMemo(
        () =>
            new Set(
                classes.flatMap((row) =>
                    row.assets.map((asset) => asset.symbol)
                )
            ),
        [classes]
    );

    const orphanHedges = React.useMemo(
        () =>
            Array.from(hedgesByAsset.entries())
                .filter(([symbol]) => !heldSymbols.has(symbol))
                .flatMap(([, group]) => group),
        [hedgesByAsset, heldSymbols]
    );

    return (
        <div className="flex flex-col gap-6">
            <CompositionBar
                segments={segments}
                loading={loading}
                activeSymbol={activeSymbol}
                onHover={setHoveredSymbol}
                onFocusChange={setFocusedSymbol}
                onReveal={revealAsset}
            />

            <div className="flex flex-col">
                {loading ? (
                    <div
                        className="flex flex-col gap-4 px-3 pt-4"
                        aria-busy="true"
                    >
                        {Array.from({ length: 2 }).map((_, index) => (
                            <Skeleton
                                key={`class-skeleton-${index}`}
                                className="h-6 w-full"
                            />
                        ))}
                    </div>
                ) : classes.length === 0 ? (
                    <ReservesEmptyState />
                ) : (
                    <Accordion
                        multiple
                        value={openClasses}
                        onValueChange={handleOpenChange}
                    >
                        {classes.map((row) => {
                            const flagged =
                                flaggedClass === row.label &&
                                !openClasses.includes(row.label);
                            return (
                                <AccordionItem
                                    key={row.label}
                                    value={row.label}
                                >
                                    <AccordionTrigger
                                        data-flagged={flagged}
                                        className={`${HIGHLIGHT_TRANSITION} ${flagged ? "bg-surface-sunken" : ""}`}
                                    >
                                        <span className="flex items-baseline gap-4 w-full">
                                            <span className="flex-1 min-w-0 inline-flex items-baseline">
                                                <Text
                                                    variant="small"
                                                    render={<span />}
                                                >
                                                    {row.label}
                                                </Text>
                                            </span>
                                            <Text
                                                variant="number"
                                                render={<span />}
                                                className={AMOUNT_COL}
                                            >
                                                {row.amountDisplay}
                                            </Text>
                                            <Text
                                                variant="number"
                                                render={<span />}
                                                className={SHARE_COL}
                                            >
                                                {row.share}
                                            </Text>
                                        </span>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <div className="flex flex-col pb-2 pr-7">
                                            {row.assets.map((asset) => (
                                                <AssetDetail
                                                    key={asset.symbol}
                                                    asset={asset}
                                                    color={
                                                        colorBySymbol.get(
                                                            asset.symbol
                                                        ) ?? paletteColor(0)
                                                    }
                                                    hedges={
                                                        hedgesByAsset.get(
                                                            asset.symbol
                                                        ) ?? []
                                                    }
                                                    active={
                                                        activeSymbol ===
                                                        asset.symbol
                                                    }
                                                    onHover={setHoveredSymbol}
                                                    onFocusChange={
                                                        setFocusedSymbol
                                                    }
                                                />
                                            ))}

                                            <ReserveHistoryChart
                                                assets={row.assets}
                                                colorBySymbol={colorBySymbol}
                                                idPrefix={`reserve-history-${row.label
                                                    .toLowerCase()
                                                    .replace(
                                                        /[^a-z0-9]+/g,
                                                        "-"
                                                    )}`}
                                            />
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            );
                        })}
                    </Accordion>
                )}
            </div>

            {orphanHedges.length > 0 && (
                <div className="flex flex-col gap-2">
                    <Text variant="small" render={<h3 />}>
                        Hedges with No Matching Holding
                    </Text>
                    {orphanHedges.map((hedge) => (
                        <div
                            key={describePosition(hedge)}
                            className="flex items-baseline gap-4 pl-3 pr-10"
                        >
                            <Text
                                variant="small"
                                render={<span />}
                                className="flex-1 min-w-0"
                            >
                                {describePosition(hedge)}
                            </Text>
                            <Text
                                variant="number"
                                render={<span />}
                                className={AMOUNT_COL}
                            >
                                {formatCurrency(notionalOf(hedge))}
                            </Text>
                            <span className={SHARE_COL} aria-hidden />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Keep the chart available for a later release without fetching hidden history.
const SHOW_CURRENT_RESERVE_HISTORY = false;

/** Current reserves have no derivative positions or asset-class grouping. */
export function StablecoinComposition({
    classes,
    loading
}: Pick<ReserveCompositionProps, "classes" | "loading">): React.ReactElement {
    const colorBySymbol = React.useMemo(() => assetColors(classes), [classes]);
    const assets = React.useMemo(
        () => classes.flatMap((row) => row.assets),
        [classes]
    );
    const [hoveredSymbol, setHoveredSymbol] = React.useState<string | null>(
        null
    );
    const [focusedSymbol, setFocusedSymbol] = React.useState<string | null>(
        null
    );
    const activeSymbol = focusedSymbol ?? hoveredSymbol;
    const segments: BarSegment[] = assets.map((asset) => ({
        label: asset.symbol,
        value: asset.amount,
        color: colorBySymbol.get(asset.symbol) ?? paletteColor(0)
    }));

    return (
        <div className="flex flex-col gap-6">
            <CompositionBar
                segments={segments}
                loading={loading}
                activeSymbol={activeSymbol}
                onHover={setHoveredSymbol}
                onFocusChange={setFocusedSymbol}
                onReveal={setFocusedSymbol}
            />
            <div className="flex flex-col">
                {loading ? (
                    <div aria-busy="true">
                        <Skeleton className="h-16 w-full" />
                    </div>
                ) : assets.length === 0 ? (
                    <ReservesEmptyState />
                ) : (
                    assets.map((asset) => (
                        <AssetDetail
                            key={asset.symbol}
                            asset={asset}
                            hedges={[]}
                            showVerification={false}
                            color={
                                colorBySymbol.get(asset.symbol) ??
                                paletteColor(0)
                            }
                            active={activeSymbol === asset.symbol}
                            onHover={setHoveredSymbol}
                            onFocusChange={setFocusedSymbol}
                        />
                    ))
                )}
            </div>
            {SHOW_CURRENT_RESERVE_HISTORY && !loading && (
                <ReserveHistoryChart
                    assets={assets}
                    idPrefix="current-reserve-history"
                    colorBySymbol={colorBySymbol}
                />
            )}
        </div>
    );
}
